import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import {
  RECOVERY_NOTICE_MS,
  useBackendStatusStore,
} from '../../store/useBackendStatusStore';

function secondsUntil(timestamp: number | null): number {
  if (timestamp === null) return 0;
  return Math.max(0, Math.ceil((timestamp - Date.now()) / 1000));
}

/**
 * Leiste am oberen Rand, sobald das Backend nicht erreichbar ist — typischerweise
 * während eines Deployments. Über AppRouter gerendert, damit sie auch auf /login und
 * den öffentlichen Seiten erscheint.
 */
export function MaintenanceBanner() {
  const { t } = useTranslation('releaseNotes');
  const status = useBackendStatusStore((s) => s.status);
  const nextRetryAt = useBackendStatusStore((s) => s.nextRetryAt);
  const recoveredAt = useBackendStatusStore((s) => s.recoveredAt);
  const probeNow = useBackendStatusStore((s) => s.probeNow);

  const [countdown, setCountdown] = useState(() => secondsUntil(nextRetryAt));
  const [showRecovery, setShowRecovery] = useState(false);

  useEffect(() => {
    if (status !== 'offline') return;
    setCountdown(secondsUntil(nextRetryAt));
    const interval = window.setInterval(() => setCountdown(secondsUntil(nextRetryAt)), 1000);
    return () => window.clearInterval(interval);
  }, [status, nextRetryAt]);

  useEffect(() => {
    if (recoveredAt === null) {
      setShowRecovery(false);
      return;
    }
    setShowRecovery(true);
    const timer = window.setTimeout(() => setShowRecovery(false), RECOVERY_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [recoveredAt]);

  if (status === 'offline') {
    return (
      <div
        className="sticky top-0 z-[95] bg-amber-50 border-b border-amber-200 px-4 py-2.5"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" strokeWidth={2} />
          <span className="text-[13px] font-medium text-amber-900">
            {t('maintenance.title')}
          </span>
          <span className="text-[12px] text-amber-700">
            {countdown > 0
              ? t('maintenance.retryIn', { seconds: countdown })
              : t('maintenance.retrying')}
          </span>
          <button
            type="button"
            onClick={() => void probeNow()}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-amber-300 text-[12px] font-medium text-amber-800 hover:bg-amber-100"
          >
            <RefreshCw className="w-3.5 h-3.5" strokeWidth={2} />
            {t('maintenance.retryNow')}
          </button>
        </div>
      </div>
    );
  }

  if (showRecovery) {
    return (
      <div
        className="sticky top-0 z-[95] bg-emerald-50 border-b border-emerald-200 px-4 py-2.5"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center gap-3">
          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" strokeWidth={2} />
          <span className="text-[13px] font-medium text-emerald-900">
            {t('maintenance.recovered')}
          </span>
        </div>
      </div>
    );
  }

  return null;
}

export default MaintenanceBanner;
